// src/services/SocketService.ts
import { Observable } from "rxjs";
import { Socket } from "socket.io-client";
import { getSocket } from "./socket";

// Type for a consult participant
interface Participant {
  ref_number: string;
  role: "subscriber" | string;
}

// Type for a consult
interface Consult {
  participants?: Participant[];
  [key: string]: any;
}

class SocketService {
  private socket: Socket;

  constructor() {
    this.socket = getSocket();
  }

  // Private helper to listen to any socket event
  public _listen<T = any>(eventName: string): Observable<T> {
    return new Observable<T>((observer) => {
      const handler = (res: T) => observer.next(res);
      this.socket.on(eventName, handler);
      return () => this.socket.off(eventName, handler);
    });
  }

  // Consult listeners
  consultListener(consult_id: string | number) {
    return this._listen(`consult:${consult_id}`);
  }

  removeConsultListener(consult_id: string | number) {
    this.socket.removeAllListeners(`consult:${consult_id}`);
  }

  liveConsultPublisherListener(ref_number: string) {
    return this._listen(`consultPublisher:${ref_number}`);
  }

  removeLiveConsultPublisherListener(ref_number: string) {
    this.socket.removeAllListeners(`consultPublisher:${ref_number}`);
  }

  liveConsultSubscriberListener(ref_number: string) {
    return this._listen(`consultSubscriber:${ref_number}`);
  }

  removeLiveConsultSubscriberListener(ref_number: string) {
    this.socket.removeAllListeners(`consultSubscriber:${ref_number}`);
  }

  liveVehicleListener(ref_number: string) {
    return this._listen(`vehicle:${ref_number}`);
  }

  removeLiveVehicleListener(ref_number: string) {
    this.socket.removeAllListeners(`vehicle:${ref_number}`);
  }

  ptzListener(consult_id: string | number) {
    return this._listen(`consultPtzAction:${consult_id}`);
  }

  removePtzListener(consult_id: string | number) {
    this.socket.removeAllListeners(`consultPtzAction:${consult_id}`);
  }

  // Emit events
  emitEvent(eventName: string, data: any) {
    console.log("🚀 Emitting event:", eventName, data);
    this.socket.emit(eventName, data);
  }

  emitConsultCreated(consult: Consult) {
    if (!consult.participants) return;

    consult.participants.forEach((p) => {
      if (!p.ref_number.includes("guest")) {
        const eventName =
          p.role === "subscriber"
            ? `consultSubscriber:${p.ref_number}`
            : `consultPublisher:${p.ref_number}`;
        this.emitEvent(eventName, consult);
      }
    });
  }
  serviceOrderListener(serviceOrderId: string | number) {
    return this._listen(`service-order-${serviceOrderId}`);
  }

  removeServiceOrderListener(serviceOrderId: string | number) {
    this.socket.removeAllListeners(`service-order-${serviceOrderId}`);
  }

  emitServiceOrderEvent(
    serviceOrderId: string | number,
    event: string,
    data: any
  ) {
    console.log("🚀 Emitting event:", event, data, serviceOrderId);
    this.socket.emit(`service-order-${serviceOrderId}`, { event, ...data });
  }
}

export default new SocketService();
